import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { DatepickerComponent } from '../../../../shared/components/datepicker/datepicker.component';
import { FormsModule } from '@angular/forms';
import { ChipComponent } from '../../../../shared/components/chip/chip.component';
import { Router } from '@angular/router';
import dayjs, { Dayjs } from 'dayjs';
import { LocaleDatetimeFormatPipe } from '../../../../shared/pipes/locale-datetime-format.pipe';
import { ShowsStore } from '../../core/stores/ShowsStore';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { ShowsService } from '../../core/services/ShowsService';
import { IShowSearchResult } from '../../core/interfaces/IShowSearchResult';
import { finalize } from 'rxjs';
import { FormControlComponent } from '../../../../shared/components/form-control/form-control.component';

@Component({
  selector: 'app-search-show-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    DatepickerComponent,
    FormsModule,
    ChipComponent,
    LocaleDatetimeFormatPipe,
    FormControlComponent,
  ],
  templateUrl: './search-show-modal.component.html',
  styleUrl: './search-show-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchShowModalComponent extends BaseModalComponent {

  public dateFrom = signal<Dayjs>(dayjs().startOf('day'));
  public dateTo = signal<Dayjs>(dayjs().add(7, 'days').startOf('day'));
  public isFromDatepickerOpened = signal(false);
  public isToDatepickerOpened = signal(false);
  public selectedAuditoriumIds = signal<number[]>([]);
  public isLoading = signal(false);
  public movieSearchTerm = '';
  public shows: IShowSearchResult[] = [];

  public readonly today = dayjs().startOf('day').toDate();
  public readonly showsStore = inject(ShowsStore);

  private readonly _router = inject(Router);
  private readonly _showsService = inject(ShowsService);

  public override open(): void {
    this.dateFrom.set(dayjs().startOf('day'));
    this.dateTo.set(dayjs().add(7, 'days').startOf('day'));
    this.selectedAuditoriumIds.set([]);
    this.movieSearchTerm = '';
    this.shows = [];

    super.open();
  }

  public toggleAuditorium(auditoriumId: number): void {
    const currentIds = this.selectedAuditoriumIds();

    if (currentIds.includes(auditoriumId)) {
      this.selectedAuditoriumIds.set(currentIds.filter(id => id !== auditoriumId));
    } else {
      this.selectedAuditoriumIds.set([...currentIds, auditoriumId]);
    }
  }

  public performSearch(): void {
    this.isLoading.set(true);
    this._showsService.searchShows(this.dateFrom(), this.dateTo(), this.selectedAuditoriumIds(), this.movieSearchTerm)
      .pipe(
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (result) => {
          this.shows = result.data;
          this.cdr.detectChanges();
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`search-shows/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public navigateToShow(showId: number): void {
    this._router.navigateByUrl(`/shows/${showId}`.langUrl());
    this.close();
  }
}
